/* eslint-disable react/display-name */
import React, { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { Row, Col, Card, Alert, Button, Form } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Sidebar from '../Sidebar'
import Status from '../Status'
import LocationApplicationType from '../../../types/LocationApplicationType'
import { getCertificationModal, setCertificationModal } from '../../../utils/storage'
import { getCertificationItemName, sortCertificationItems } from '../../../utils/dataTools'


interface Props {
  locationApplication: LocationApplicationType;
  onOpenModal: (modalToOpen: string, modalHeader: string, readonly: boolean) => void;
  onOpenRejectionNote: (row: any) => void;
  onViewInstruction: (row: any) => void;
  readOnly: boolean;
}

const LocationCertification: FC<Props> = ({ locationApplication, onOpenModal, onOpenRejectionNote, onViewInstruction, readOnly }: Props) => {
  const localCertificationModal = getCertificationModal()
  const [showAlert, setShowAlert] = useState(!localCertificationModal)
  const [welcomeLanguage, setWelcomeLanguage] = useState('English')

  const { applicationWelcomeAlert } = useSelector((state: any) => ({
    applicationWelcomeAlert: state.applicationWelcomeAlert.data
  }))

  const handleCloseModal = () => {
    setShowAlert(false)
    setCertificationModal()
  }

  const inProgressColumns = [{
    dataField: 'status',
    text: 'Status',
    sort: false,
    formatter: (cellContent, row) => cellContent === 'SUBMITTED' ? <FontAwesomeIcon icon={['fas', 'check-circle']} size='2x' color='#75b465' /> : <FontAwesomeIcon icon={['fas', 'circle']} size='2x' color='#cccccc' />,
    headerStyle: (column, colIndex) => {
      return { width: '10%' }
    }
  }, {
    dataField: 'certificationType',
    text: 'Certification Item',
    sort: false,
    formatter: (cellContent) => getCertificationItemName(cellContent),
    headerStyle: (column, colIndex) => {
      return { width: '75%' }
    }
  }, {
    dataField: 'actions',
    text: 'Actions',
    sort: false,
    formatter: (cellContent, row) => {
      if (readOnly) {
        return (
          <Button className="action-button" variant="secondary" disabled>
            View
          </Button>
        );
      }

      if (row.modalType === 'instruction' || !row.modalType) {
        return <Button className='action-button' variant='info' onClick={() => onViewInstruction(row)}>View</Button>;
      }

      return row.status === 'SUBMITTED' ? (
        <Button
          className='action-button'
          variant='info'
          onClick={() => onOpenModal(row.modalType, row.certificationType, false)}
        >
          View/Edit
        </Button>
      ) : (
        <Button
          className='action-button'
          variant='info'
          onClick={() => onOpenModal(row.modalType, row.certificationType, false)}
        >
          Start
        </Button>
      );
    }
  }];

  const columns = [{
    dataField: 'certificationType',
    text: 'Certification Item',
    sort: false,
    formatter: (cellContent) => getCertificationItemName(cellContent),
    headerStyle: (column, colIndex) => {
      return { width: '45%' }
    }
  }, {
    dataField: 'status',
    text: 'Approval Status',
    sort: false,
    formatter: (cellContent, row) => row.modalType === 'instruction' || !row.modalType ? null : <Status cellContent={cellContent} row={row} />
  }, {
    dataField: 'actions',
    text: 'Actions',
    sort: false,
    formatter: (cellContent, row) => {
      if (readOnly) {
        return (
          <Button className="action-button" variant="secondary" disabled>
            View
          </Button>
        );
      }

      return row.modalType === 'instruction' || !row.modalType ? (
        <Button
          className="action-button"
          variant="outline-secondary"
          onClick={() => onViewInstruction(row)}
        >
          View
        </Button>
      ) : row.status === 'REJECTED' ? (
        <>
          <Button
            className="action-button"
            variant="outline-secondary"
            onClick={() => onOpenModal(row.modalType, row.certificationType, false)}
          >
            View/Edit
          </Button>
          <Button
            className="action-button"
            variant="outline-secondary"
            onClick={() => onOpenRejectionNote(row)}
          >
            Note
          </Button>
        </>
      ) : (
        <Button
          className="action-button"
          variant="outline-secondary"
          onClick={() => onOpenModal(row.modalType, row.certificationType, true)}
        >
          View
        </Button>
      );
    },
    headerStyle: (column, colIndex) => {
      return { width: '20%' }
    }
  }];

  const welcomeMessage = applicationWelcomeAlert.filter((alert) => alert.type === 'certification').find(alert => alert.language === welcomeLanguage)

  return (
    <div>
      <Row>
        <Col xs={3}>
          <Sidebar
            label='Certification'
            type='Residential'
            startDate={(locationApplication && locationApplication.certificationStartDate) || null}
            submitDate={(locationApplication && locationApplication.certificationSubmitDate) || null}
            approvalDate={(locationApplication && locationApplication.certificationApprovalDate) || null}
          />
        </Col>
        <Col xs={9}>
          {
            showAlert ? (
              <Alert variant='info' dismissible onClose={handleCloseModal}>
                <div className='applicant-welcome-alert-title'>
                  <h5>Welcome to the Lakeland Care Inc. Provider Application Process</h5>
                  <Form.Control as="select" className='applicant-welcome-language-select' value={welcomeLanguage} onChange={(e: any) => setWelcomeLanguage(e.target.value)}>
                    {applicationWelcomeAlert.filter((alert) => alert.type === 'certification').map(alert => <option key={alert.language}>{alert.language}</option>)}
                  </Form.Control>
                </div>
                <div dangerouslySetInnerHTML={{ __html: (welcomeMessage && welcomeMessage.description) || '' }} ></div>
              </Alert>
            ) : null
          }
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <Card.Title>Certification Items</Card.Title>
              <div>
                <ToolkitProvider
                  keyField='locationApplicationCertificationId'
                  bootstrap4
                  data={sortCertificationItems(locationApplication && locationApplication.certification)}
                  columns={(locationApplication && locationApplication.certificationStatus === 'STARTED') ? inProgressColumns : columns}
                  search
                >
                  {
                    (props: any) => (
                      <div className='application-table-container' style={{ width: '100%', background: 'white' }}>
                        <BootstrapTable
                          id='application_table'
                          bordered={false}
                          condensed
                          bootstrap4
                          noDataIndication={() => <div>No items found</div>}
                          {...props.baseProps}
                        />
                      </div>
                    )
                  }
                </ToolkitProvider>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default LocationCertification